import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import {
  theme,
  Panel,
  Heading,
  useResponsive,
  Icon,
} from "@clearabee/ui-library-native";
import { ButtonLink } from "components/Core/ButtonLink/ButtonLink";
import { SERVICES } from "lib/constants/services";
import placeholderImage from "lib/images/placeholder.png";
import { Image } from "components/Image/Image";
import { styles, ids } from "./BookServicePill.styles";
import { Anchor } from "components/Core/Anchor/Anchor";
import type { AnchorProps } from "components/Core/Anchor/Anchor.types";

const ConditionalAnchor = (props: AnchorProps): React.ReactElement => {
  return Platform.OS === "web" ? <>{props.children}</> : <Anchor {...props} />;
};

export const BookServicePill = (): React.ReactElement => {
  const [translate] = useTranslation();
  const { screens } = useResponsive();

  return (
    <ConditionalAnchor routeName="Book">
      <Panel
        styles={styles.panel}
        dataSet={{ media: ids.panel }}
        shadow={false}
      >
        <View style={styles.headingWrap} dataSet={{ media: ids.headingWrap }}>
          <Heading fontSize="xsmall2" styles={styles.subheading}>
            {translate("hwa.screens.dashboard.userDashboard.gotYouCovered")}
          </Heading>
          <Heading
            fontSize="base"
            level={5}
            color="brand"
            styles={{ fontFamily: "ProximaSemiBold" }}
          >
            {translate("hwa.screens.dashboard.userDashboard.bookService")}
          </Heading>
        </View>
        <View
          style={styles.serviceImages}
          dataSet={{ media: ids.serviceImages }}
        >
          {SERVICES.map((service, index) => {
            if (Platform.OS !== "web") {
              return (
                <View
                  key={`${service.sku || "service"}-${index}`}
                  style={styles.imageContainer}
                >
                  <Image
                    source={service.image ?? placeholderImage}
                    resizeMode="contain"
                    style={styles.image}
                  />
                </View>
              );
            }

            return (
              <Anchor
                key={`${service.sku || "service"}-${index}`}
                {...(service.sku
                  ? {
                      routeName: "ServicePage",
                      params: { categorySku: service.sku },
                    }
                  : { routeName: "Redirect", params: { url: service.link } })}
              >
                <View key={service.sku} style={styles.imageContainer}>
                  <Image
                    source={service.image ?? placeholderImage}
                    resizeMode="contain"
                    style={styles.image}
                  />
                </View>
              </Anchor>
            );
          })}
        </View>
        {screens.medium && (
          <View style={{ flex: 1 }}>
            <ButtonLink routeName="Book" color="warning" size="medium">
              {translate("hwa.screens.dashboard.userDashboard.bookNow")}
            </ButtonLink>
          </View>
        )}
        {!screens.medium && (
          <View
            style={{
              position: "absolute",
              top: theme.spacing.small,
              right: theme.spacing.small,
            }}
          >
            <Icon.Chevron color="brand.base" size="medium" />
          </View>
        )}
      </Panel>
    </ConditionalAnchor>
  );
};
